import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  FormControl,
  FormGroup,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  CircularProgress,
  Button
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import CategoryIcon from '@mui/icons-material/Category';
import ExtensionIcon from '@mui/icons-material/Extension';
import ScienceIcon from '@mui/icons-material/Science';
import { WizardDialog } from '../WizardDialog';
import { WizardStep } from '../WizardStep';
import { useWizard } from '../../contexts/WizardContext';
import { ContentPreviewPanel, ContentItem } from '../wizard-panels/ContentPreviewPanel';
import { ConflictResolutionPanel, Conflict, ConflictResolutionStrategy } from '../wizard-panels/ConflictResolutionPanel';
import { ProgressSummaryPanel, SummarySection } from '../wizard-panels/ProgressSummaryPanel';
import { moodleApi, MoodleSource, RC5AU } from '../../services/moodle-api';
import { basePrefixApi, contentApi, versionApi, csvApi } from '../../services/api';
import { useJobs } from '../../contexts/JobsContext';
import { ScenarioSelectionDialog } from '../ScenarioSelectionDialog';
import { rangeosEnvironmentService } from '../../services/rangeos-environment-api';

export interface RestoreMoodleFromS3WizardProps {
  open: boolean;
  onClose: () => void;
  onComplete?: (jobId: string) => void;
}

const WIZARD_STEPS = [
  { label: 'Select S3 Source', description: 'Choose S3 environment to restore from' },
  { label: 'Select Destination', description: 'Choose target Moodle instance' },
  { label: 'Select Content', description: 'Choose courses to restore' },
  { label: 'Select Versions', description: 'Choose specific versions' },
  { label: 'Map Scenarios', description: 'Map CMI5 activities to RangeOS scenarios' },
  { label: 'Configure Restore', description: 'Set restore options and resolve conflicts' },
  { label: 'Review & Confirm', description: 'Review and start restore' }
];

// Helper function to convert filename to course name
const formatCourseName = (filename: string): string => {
  // Remove -Course.mbz suffix and convert hyphens to spaces
  const name = filename
    .replace(/-Course\.mbz$/, '')
    .replace(/-/g, ' ');

  // Capitalize each word
  return name
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

interface S3Content {
  filename: string;
  size: number;
  lastModified: string;
  hasVersions?: boolean;
  category?: string; // Original Moodle category
  type?: 'course' | 'framework' | 'template' | 'category'; // Content type
  versions?: Array<{
    versionId: string;
    lastModified: string;
    size: number;
    description?: string;
  }>;
}

// Step 1: Select S3 Environment
const S3SourceSelectionStep: React.FC = () => {
  const wizard = useWizard();
  const [environments, setEnvironments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadEnvironments = async () => {
      try {
        const data = await basePrefixApi.getEnvironmentsWithBuckets();
        setEnvironments(data.map(env => ({
          id: env.environmentId,
          name: env.environmentName,
          basePrefix: env.prefix,
          bucketId: env.bucketId
        })));
      } catch (err: any) {
        wizard.setError(`Failed to load S3 environments: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
    loadEnvironments();
  }, []);

  useEffect(() => {
    const environmentId = wizard.getData('s3EnvironmentId');
    const isValid = !!environmentId && environments.some(e => e.id === environmentId);
    wizard.setStepValidation(0, isValid);
  }, [wizard.data, environments]);

  const handleEnvironmentChange = async (envId: string) => {
    wizard.setData('s3EnvironmentId', envId);
    const env = environments.find(e => e.id === envId);
    console.log('[WIZARD] Environment selected:', { envId, env, basePrefix: env?.basePrefix, bucketId: env?.bucketId });
    wizard.setData('s3EnvironmentName', env?.name);
    wizard.setData('s3EnvironmentPrefix', env?.basePrefix); // Store the S3 prefix (folder name)
    wizard.setData('s3BucketId', env?.bucketId); // Store the bucket ID

    // Tell the backend to switch to this environment (which switches both bucket and prefix)
    try {
      await basePrefixApi.setEnvironmentById(envId);
      console.log('[WIZARD] Successfully switched backend to environment:', envId);
    } catch (err: any) {
      console.error('[WIZARD] Failed to set backend environment:', err);
      wizard.setError(`Failed to switch to environment: ${err.message}`);
    }

    // Clear downstream selections
    wizard.setData('selectedContent', []);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <FormControl fullWidth>
        <InputLabel>S3 Environment</InputLabel>
        <Select
          value={wizard.getData('s3EnvironmentId', '')}
          onChange={(e) => handleEnvironmentChange(e.target.value)}
          label="S3 Environment"
        >
          {environments.map(env => (
            <MenuItem key={env.id} value={env.id}>
              <Box>
                <Typography variant="body1">{env.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {env.basePrefix}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {environments.length === 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          No S3 environments configured. Please add an S3 environment in the Config section first.
        </Alert>
      )}

      {wizard.getData('s3EnvironmentId') && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Selected source: <strong>{wizard.getData('s3EnvironmentName')}</strong>
        </Alert>
      )}
    </Box>
  );
};

// Step 2: Select Content
const ContentSelectionStep: React.FC = () => {
  const wizard = useWizard();
  const [contentTypes, setContentTypes] = useState<string[]>(wizard.getData('contentTypes', []));
  const [courses, setCourses] = useState<S3Content[]>([]);
  const [categories, setCategories] = useState<S3Content[]>([]);
  const [frameworks, setFrameworks] = useState<S3Content[]>([]);
  const [templates, setTemplates] = useState<S3Content[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingContent, setLoadingContent] = useState<Record<string, boolean>>({});
  const [selectedCourses, setSelectedCourses] = useState<string[]>(wizard.getData('selectedCourses', []));
  const [selectedFrameworks, setSelectedFrameworks] = useState<string[]>(wizard.getData('selectedFrameworks', []));
  const [selectedTemplates, setSelectedTemplates] = useState<string[]>(wizard.getData('selectedTemplates', []));
  const [searchTerm, setSearchTerm] = useState('');

  // Check what content types are available in S3
  useEffect(() => {
    const checkAvailableContent = async () => {
      setLoading(true);
      try {
        // Get the selected environment ID (preferred) or prefix (legacy)
        const environment = wizard.getData('s3EnvironmentId') || wizard.getData('s3EnvironmentPrefix');
        console.log('[WIZARD] Loading content with environment:', environment, 'wizardData:', {
          s3EnvironmentPrefix: wizard.getData('s3EnvironmentPrefix'),
          s3EnvironmentId: wizard.getData('s3EnvironmentId')
        });

        const [coursesData, categoriesData, frameworksData, templatesData] = await Promise.all([
          contentApi.getFolderContentsWithVersions('courses', environment).catch(() => []),
          contentApi.getFolderContentsWithVersions('categories', environment).catch(() => []),
          contentApi.getFolderContentsWithVersions('competency-frameworks', environment).catch(() => []),
          contentApi.getFolderContentsWithVersions('learning-path-templates', environment).catch(() => [])
        ]);

        // Set courses
        if (coursesData.length > 0) {
          const s3Courses: S3Content[] = coursesData.map(item => ({
            filename: item.name,
            size: item.size,
            lastModified: item.lastModified,
            hasVersions: item.versionInfo?.hasVersionHistory || false,
            category: item.metadata?.courseCategory || item.metadata?.category,
            type: 'course' as const
          }));
          setCourses(s3Courses);
        }

        // Set categories
        if (categoriesData.length > 0) {
          const s3Categories: S3Content[] = categoriesData.map(item => ({
            filename: item.name,
            size: item.size,
            lastModified: item.lastModified,
            hasVersions: item.versionInfo?.hasVersionHistory || false,
            type: 'category' as const
          }));
          setCategories(s3Categories);
        }

        // Set frameworks
        if (frameworksData.length > 0) {
          const s3Frameworks: S3Content[] = frameworksData.map(item => ({
            filename: item.name,
            size: item.size,
            lastModified: item.lastModified,
            hasVersions: item.versionInfo?.hasVersionHistory || false,
            type: 'framework' as const
          }));
          setFrameworks(s3Frameworks);
        }

        // Set templates
        if (templatesData.length > 0) {
          const s3Templates: S3Content[] = templatesData.map(item => ({
            filename: item.name,
            size: item.size,
            lastModified: item.lastModified,
            hasVersions: item.versionInfo?.hasVersionHistory || false,
            type: 'template' as const
          }));
          setTemplates(s3Templates);
        }
      } catch (err: any) {
        wizard.setError(`Failed to load content from S3: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
    checkAvailableContent();
  }, []);

  useEffect(() => {
    wizard.setData('contentTypes', contentTypes);
    wizard.setData('selectedCourses', selectedCourses);
    wizard.setData('selectedFrameworks', selectedFrameworks);
    wizard.setData('selectedTemplates', selectedTemplates);

    // Build selected content array including all selected content types
    const selectedContent: S3Content[] = [];

    if (contentTypes.includes('courses')) {
      selectedContent.push(...courses.filter(c => selectedCourses.includes(c.filename)));
    }

    // Add selected frameworks
    if (contentTypes.includes('frameworks')) {
      selectedContent.push(...frameworks.filter(f => selectedFrameworks.includes(f.filename)));
    }

    // Add selected templates
    if (contentTypes.includes('learning-paths')) {
      selectedContent.push(...templates.filter(t => selectedTemplates.includes(t.filename)));
    }

    // Add all categories if categories is selected
    if (contentTypes.includes('categories')) {
      selectedContent.push(...categories);
    }

    wizard.setData('selectedContent', selectedContent);

    // Step is valid if at least one content type is selected
    // And each selected type has at least one item selected
    const hasValidSelection = contentTypes.length > 0 && (
      (!contentTypes.includes('courses') || selectedCourses.length > 0) &&
      (!contentTypes.includes('frameworks') || selectedFrameworks.length > 0) &&
      (!contentTypes.includes('learning-paths') || selectedTemplates.length > 0)
    );
    wizard.setStepValidation(2, hasValidSelection);
  }, [contentTypes, selectedCourses, selectedFrameworks, selectedTemplates, courses, categories, frameworks, templates]);

  const handleContentTypeChange = (type: string) => {
    setContentTypes(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const toggleCourse = (filename: string) => {
    setSelectedCourses(prev =>
      prev.includes(filename)
        ? prev.filter(f => f !== filename)
        : [...prev, filename]
    );
  };

  const toggleAllCourses = () => {
    if (selectedCourses.length === filteredCourses.length) {
      setSelectedCourses([]);
    } else {
      setSelectedCourses(filteredCourses.map(c => c.filename));
    }
  };

  const toggleFramework = (filename: string) => {
    setSelectedFrameworks(prev =>
      prev.includes(filename)
        ? prev.filter(f => f !== filename)
        : [...prev, filename]
    );
  };

  const toggleAllFrameworks = () => {
    if (selectedFrameworks.length === frameworks.length) {
      setSelectedFrameworks([]);
    } else {
      setSelectedFrameworks(frameworks.map(f => f.filename));
    }
  };

  const toggleTemplate = (filename: string) => {
    setSelectedTemplates(prev =>
      prev.includes(filename)
        ? prev.filter(f => f !== filename)
        : [...prev, filename]
    );
  };

  const toggleAllTemplates = () => {
    if (selectedTemplates.length === templates.length) {
      setSelectedTemplates([]);
    } else {
      setSelectedTemplates(templates.map(t => t.filename));
    }
  };

  const filteredCourses = courses.filter(course =>
    searchTerm === '' ||
    course.filename.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom fontWeight="bold">
        Select Content Types to Restore
      </Typography>

      <FormGroup>
        <FormControlLabel
          control={
            <Checkbox
              checked={contentTypes.includes('courses')}
              onChange={() => handleContentTypeChange('courses')}
              disabled={courses.length === 0}
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SchoolIcon fontSize="small" color="primary" />
              <Typography>Courses</Typography>
              <Chip label={courses.length} size="small" variant="outlined" />
            </Box>
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={contentTypes.includes('categories')}
              onChange={() => handleContentTypeChange('categories')}
              disabled={categories.length === 0}
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CategoryIcon fontSize="small" color="primary" />
              <Typography>Categories</Typography>
              <Chip label={categories.length} size="small" variant="outlined" />
            </Box>
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={contentTypes.includes('frameworks')}
              onChange={() => handleContentTypeChange('frameworks')}
              disabled={frameworks.length === 0}
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ExtensionIcon fontSize="small" color="primary" />
              <Typography>Competency Frameworks</Typography>
              <Chip label={frameworks.length} size="small" variant="outlined" />
            </Box>
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={contentTypes.includes('learning-paths')}
              onChange={() => handleContentTypeChange('learning-paths')}
              disabled={templates.length === 0}
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ExtensionIcon fontSize="small" color="secondary" />
              <Typography>Learning Path Templates</Typography>
              <Chip label={templates.length} size="small" variant="outlined" />
            </Box>
          }
        />
      </FormGroup>

      {contentTypes.includes('courses') && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            Select Courses
          </Typography>

          <TextField
            fullWidth
            label="Search courses"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by filename..."
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Checkbox
              checked={selectedCourses.length === filteredCourses.length && filteredCourses.length > 0}
              indeterminate={selectedCourses.length > 0 && selectedCourses.length < filteredCourses.length}
              onChange={toggleAllCourses}
            />
            <Typography variant="body2" color="text.secondary">
              {selectedCourses.length} of {courses.length} courses selected
            </Typography>
          </Box>

          {filteredCourses.length === 0 ? (
            <Alert severity="info">
              {searchTerm ? 'No courses match your search.' : 'No courses found in this S3 environment.'}
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" width={50}></TableCell>
                    <TableCell>Course Name</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Size</TableCell>
                    <TableCell>Last Modified</TableCell>
                    <TableCell>Versions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCourses.map((course) => (
                    <TableRow
                      key={course.filename}
                      hover
                      onClick={() => toggleCourse(course.filename)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox checked={selectedCourses.includes(course.filename)} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {formatCourseName(course.filename)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {course.filename}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {course.category || 'Miscellaneous'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {(course.size / 1024 / 1024).toFixed(2)} MB
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(course.lastModified).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {course.hasVersions && (
                          <Chip label="Versioned" size="small" color="primary" variant="outlined" />
                        )}
                      </TableCell>
                    </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
          )}
        </Box>
      )}

      {contentTypes.includes('frameworks') && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            Select Competency Frameworks
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Checkbox
              checked={selectedFrameworks.length === frameworks.length && frameworks.length > 0}
              indeterminate={selectedFrameworks.length > 0 && selectedFrameworks.length < frameworks.length}
              onChange={toggleAllFrameworks}
            />
            <Typography variant="body2" color="text.secondary">
              {selectedFrameworks.length} of {frameworks.length} frameworks selected
            </Typography>
          </Box>

          {frameworks.length === 0 ? (
            <Alert severity="info">No competency frameworks found in this S3 environment.</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" width={50}></TableCell>
                    <TableCell>Framework Name</TableCell>
                    <TableCell>Size</TableCell>
                    <TableCell>Last Modified</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {frameworks.map(framework => (
                    <TableRow
                      key={framework.filename}
                      hover
                      onClick={() => toggleFramework(framework.filename)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox checked={selectedFrameworks.includes(framework.filename)} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {framework.filename}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {(framework.size / 1024 / 1024).toFixed(2)} MB
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(framework.lastModified).toLocaleString()}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {contentTypes.includes('learning-paths') && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            Select Learning Path Templates
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedTemplates.length === templates.length && templates.length > 0}
                  indeterminate={selectedTemplates.length > 0 && selectedTemplates.length < templates.length}
                  onChange={toggleAllTemplates}
                />
              }
              label="Select All Templates"
            />
            <Typography variant="body2" color="text.secondary">
              {selectedTemplates.length} of {templates.length} templates selected
            </Typography>
          </Box>

          {templates.length === 0 ? (
            <Alert severity="info">No learning path templates found in this S3 environment.</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" width={50}></TableCell>
                    <TableCell>Template Name</TableCell>
                    <TableCell>Size</TableCell>
                    <TableCell>Last Modified</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {templates.map(template => (
                    <TableRow
                      key={template.filename}
                      hover
                      onClick={() => toggleTemplate(template.filename)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox checked={selectedTemplates.includes(template.filename)} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {template.filename}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {(template.size / 1024 / 1024).toFixed(2)} MB
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(template.lastModified).toLocaleString()}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}
    </Box>
  );
};

// Step 3: Select Versions (for versioned content)
const VersionSelectionStep: React.FC = () => {
  const wizard = useWizard();
  const selectedContent: S3Content[] = wizard.getData('selectedContent', []);
  const [versionSelections, setVersionSelections] = useState<Record<string, string>>(
    wizard.getData('versionSelections', {})
  );
  const [loadingVersions, setLoadingVersions] = useState<Record<string, boolean>>({});

  useEffect(() => {
    // Initialize version selections with 'current' for all versioned content
    const initialSelections: Record<string, string> = {};
    selectedContent.forEach(content => {
      if (content.hasVersions) {
        // Set to 'current' if not already selected
        if (!versionSelections[content.filename]) {
          initialSelections[content.filename] = 'current';
        }
      }
    });

    // Update state if we have new selections
    if (Object.keys(initialSelections).length > 0) {
      setVersionSelections(prev => ({ ...prev, ...initialSelections }));
    }

    // Load version info for selected content
    const loadVersions = async () => {
      const envId = wizard.getData('s3EnvironmentId');
      for (const content of selectedContent) {
        if (content.hasVersions && !content.versions) {
          setLoadingVersions(prev => ({ ...prev, [content.filename]: true }));
          try {
            const versions = await versionApi.getVersionHistory('courses', content.filename);
            // Map to expected format
            const mappedVersions = versions.map(v => ({
              versionId: v.version, // Use 'version' field from VersionMetadata
              lastModified: v.timestamp,
              size: v.size,
              description: v.description
            }));
            // Update content with versions
            const updated = selectedContent.map(c =>
              c.filename === content.filename ? { ...c, versions: mappedVersions } : c
            );
            wizard.setData('selectedContent', updated);
          } catch (err: any) {
            console.error(`Failed to load versions for ${content.filename}:`, err);
          } finally {
            setLoadingVersions(prev => ({ ...prev, [content.filename]: false }));
          }
        }
      }
    };
    loadVersions();
  }, [selectedContent.length]);

  useEffect(() => {
    wizard.setData('versionSelections', versionSelections);
    // Step is valid if all versioned content has a version selected
    const allVersionsSelected = selectedContent.every(content =>
      !content.hasVersions || versionSelections[content.filename]
    );
    wizard.setStepValidation(3, allVersionsSelected);
  }, [versionSelections, selectedContent]);

  const handleVersionChange = (filename: string, versionId: string) => {
    setVersionSelections(prev => ({ ...prev, [filename]: versionId }));
  };

  const versionedContent = selectedContent.filter(c => c.hasVersions);

  if (versionedContent.length === 0) {
    // Auto-validate if no versioned content
    useEffect(() => {
      wizard.setStepValidation(3, true);
    }, []);

    return (
      <Alert severity="info">
        None of the selected courses have versioned content. You can proceed to the next step.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom>
        Select Versions for Versioned Content
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Some courses have multiple versions. Select which version to restore for each.
      </Typography>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Course</TableCell>
              <TableCell>Version</TableCell>
              <TableCell>Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {versionedContent.map((content) => (
              <TableRow key={content.filename}>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {formatCourseName(content.filename)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {content.filename}
                  </Typography>
                </TableCell>
                <TableCell>
                  {loadingVersions[content.filename] ? (
                    <CircularProgress size={20} />
                  ) : (
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <Select
                        value={versionSelections[content.filename] || 'current'}
                        onChange={(e) => handleVersionChange(content.filename, e.target.value)}
                      >
                        <MenuItem value="current">Current (Latest)</MenuItem>
                        {content.versions?.map((version) => (
                          <MenuItem key={version.versionId} value={version.versionId}>
                            Version {version.versionId.substring(0, 8)}...
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </TableCell>
                <TableCell>
                  {versionSelections[content.filename] && versionSelections[content.filename] !== 'current' && (
                    <Box>
                      {content.versions?.find(v => v.versionId === versionSelections[content.filename])?.description && (
                        <Typography variant="caption" display="block">
                          {content.versions.find(v => v.versionId === versionSelections[content.filename])?.description}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {content.versions?.find(v => v.versionId === versionSelections[content.filename])?.lastModified}
                      </Typography>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Button
        variant="outlined"
        size="small"
        onClick={() => {
          const selections: Record<string, string> = {};
          versionedContent.forEach(c => {
            selections[c.filename] = 'current';
          });
          setVersionSelections(selections);
        }}
        sx={{ mt: 2 }}
      >
        Select Current for All
      </Button>
    </Box>
  );
};

// Step 4: Map Scenarios (CMI5 Activities)
const ScenarioMappingStep: React.FC = () => {
  const wizard = useWizard();
  const [loading, setLoading] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [cmi5Courses, setCmi5Courses] = useState<Array<{
    courseFilename: string;
    hasCMI5: boolean;
    activities: Array<{
      activityName: string;
      activityId: string;
      aus: RC5AU[];
    }>;
  }>>([]);
  const [allAUs, setAllAUs] = useState<RC5AU[]>([]);
  const [scenarioDialogOpen, setScenarioDialogOpen] = useState(false);
  const [rangeosEnvironments, setRangeosEnvironments] = useState<any[]>([]);
  const [selectedEnvironmentId, setSelectedEnvironmentId] = useState<string>('');
  const detectionRunRef = useRef(false); // Track if detection has already run

  const selectedContent: S3Content[] = wizard.getData('selectedContent', []);
  const courseFiles = selectedContent.filter(c => c.type === 'course');

  // Detect CMI5 activities in selected courses
  useEffect(() => {
    const detectCMI5 = async () => {
      // Prevent running detection multiple times
      if (detectionRunRef.current) {
        return;
      }

      if (courseFiles.length === 0) {
        wizard.setStepValidation(4, true); // Auto-validate if no courses
        detectionRunRef.current = true;
        return;
      }

      setDetecting(true);
      detectionRunRef.current = true;
      try {
        const s3EnvironmentId = wizard.getData('s3EnvironmentId') || wizard.getData('s3EnvironmentPrefix');
        const courseFilenames = courseFiles.map(c => c.filename);

        const result = await moodleApi.detectCMI5InS3Courses(courseFilenames, s3EnvironmentId);

        const coursesWithActivities = result.coursesWithCMI5.filter(c => c.hasCMI5);
        setCmi5Courses(coursesWithActivities);

        // Collect all AUs from all activities
        const aus: RC5AU[] = [];
        coursesWithActivities.forEach(course => {
          course.activities.forEach(activity => {
            activity.aus.forEach(au => {
              aus.push({
                ...au,
                // Add metadata to track which course/activity this AU belongs to
                metadata: {
                  courseFilename: course.courseFilename,
                  activityName: activity.activityName,
                  activityId: activity.activityId
                } as any
              });
            });
          });
        });

        setAllAUs(aus);

        // Auto-populate scenario mappings from detected AU linkages
        // AUs with rangeosScenarioUUID already have a linked draft scenario stored in S3
        const autoMappings: Record<string, {
          scenarioId: string;
          scenarioName: string;
          mappingType: 'draft' | 'existing';
          draftScenarioUuid?: string;
          draftScenarioVersion?: string;
        }> = {};
        aus.forEach(au => {
          if (au.rangeosScenarioUUID && au.rangeosScenarioName) {
            autoMappings[au.id] = {
              scenarioId: au.rangeosScenarioUUID,
              scenarioName: au.rangeosScenarioName,
              mappingType: 'draft',
              draftScenarioUuid: au.rangeosScenarioUUID,
              draftScenarioVersion: 'current',
            };
          }
        });
        if (Object.keys(autoMappings).length > 0) {
          wizard.setData('scenarioMappings', autoMappings);
        }

        // Save CMI5 detection results to wizard data
        wizard.setData('cmi5Courses', coursesWithActivities);
        wizard.setData('hasCMI5Activities', coursesWithActivities.length > 0);

        // Load RangeOS environments
        const environments = await rangeosEnvironmentService.getEnvironments();
        setRangeosEnvironments(environments);

        // Check if environment was already auto-detected from destination selection
        const autoDetectedEnvId = wizard.getData('rangeosEnvironmentId');

        if (autoDetectedEnvId) {
          // Use the auto-detected environment from destination
          setSelectedEnvironmentId(autoDetectedEnvId);
          console.log(`Using auto-detected RangeOS environment from destination: ${autoDetectedEnvId}`);
        } else if (environments.length > 0 && !selectedEnvironmentId) {
          // Fallback: auto-select first environment if none was auto-detected
          const defaultEnvId = environments[0].id;
          setSelectedEnvironmentId(defaultEnvId);
          wizard.setData('rangeosEnvironmentId', defaultEnvId);
          console.log(`No auto-detected environment, using first available: ${defaultEnvId}`);
        }

        // Auto-validate:
        // - If no CMI5 activities, always validate
        // - If CMI5 activities exist and RangeOS environment is selected, validate (scenarios will be auto-imported)
        if (aus.length === 0 || (aus.length > 0 && environments.length > 0)) {
          wizard.setStepValidation(4, true);
        }
      } catch (error) {
        console.error('Failed to detect CMI5 activities:', error);
        wizard.setError('Failed to detect CMI5 activities in courses');
        wizard.setStepValidation(4, true); // Allow proceeding even if detection fails
      } finally {
        setDetecting(false);
      }
    };

    detectCMI5();
  }, [courseFiles.length]);

  const handleOpenScenarioDialog = () => {
    setScenarioDialogOpen(true);
  };

  const handleSaveScenarioMappings = (
    mappings: Array<{
      auId: string;
      scenarioId: string;
      scenarioName: string;
      mappingType: 'draft' | 'existing';
      draftScenarioUuid?: string;
      draftScenarioVersion?: string;
    }>,
    environmentId: string
  ) => {
    // Store mappings in wizard data with full information
    const mappingsMap: Record<string, {
      scenarioId: string;
      scenarioName: string;
      mappingType: 'draft' | 'existing';
      draftScenarioUuid?: string;
      draftScenarioVersion?: string;
    }> = {};
    mappings.forEach(m => {
      mappingsMap[m.auId] = {
        scenarioId: m.scenarioId,
        scenarioName: m.scenarioName,
        mappingType: m.mappingType,
        draftScenarioUuid: m.draftScenarioUuid,
        draftScenarioVersion: m.draftScenarioVersion,
      };
    });

    wizard.setData('scenarioMappings', mappingsMap);
    wizard.setData('rangeosEnvironmentId', environmentId);
    wizard.setStepValidation(4, true);
    setScenarioDialogOpen(false);
  };

  const selectedEnvironment = rangeosEnvironments.find(e => e.id === selectedEnvironmentId);
  const scenarioMappings = wizard.getData('scenarioMappings', {});

  if (detecting) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Detecting CMI5 activities in selected courses...
        </Typography>
      </Box>
    );
  }

  if (allAUs.length === 0) {
    return (
      <Box>
        <Alert severity="info">
          No CMI5 activities with scenario mappings found in the selected courses. You can proceed to the next step.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom>
        CMI5 Activities Detected
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        The selected courses contain CMI5 activities that need to be mapped to RangeOS scenarios.
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          Found <strong>{allAUs.length} AUs</strong> in <strong>{cmi5Courses.length} courses</strong> with scenario linkages.
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          During restore, linked scenarios will be automatically imported to the selected RangeOS environment.
        </Typography>
      </Alert>

      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Select Target RangeOS Environment
        </Typography>
        <FormControl fullWidth>
          <InputLabel>RangeOS Environment</InputLabel>
          <Select
            value={selectedEnvironmentId}
            onChange={(e) => {
              const newEnvId = e.target.value;
              setSelectedEnvironmentId(newEnvId);
              wizard.setData('rangeosEnvironmentId', newEnvId);
            }}
            label="RangeOS Environment"
          >
            {rangeosEnvironments.map(env => (
              <MenuItem key={env.id} value={env.id}>
                {env.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {selectedEnvironmentId && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography variant="body2">
            ✓ Scenarios will be automatically imported to <strong>{selectedEnvironment?.name}</strong> during restore
          </Typography>
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <Button
          variant="outlined"
          onClick={handleOpenScenarioDialog}
          startIcon={<ScienceIcon />}
          disabled={!selectedEnvironmentId}
          size="small"
        >
          View/Edit Scenario Mappings ({Object.keys(scenarioMappings).length} / {allAUs.length} configured)
        </Button>

        {Object.keys(scenarioMappings).length > 0 && (
          <Chip
            label={`${Object.keys(scenarioMappings).length} configured`}
            color="primary"
            size="small"
          />
        )}
      </Box>

      {scenarioDialogOpen && selectedEnvironmentId && (
        <ScenarioSelectionDialog
          open={scenarioDialogOpen}
          onClose={() => setScenarioDialogOpen(false)}
          aus={allAUs}
          environmentId={selectedEnvironmentId}
          environmentName={selectedEnvironment?.name}
          s3BucketId={wizard.getData('s3BucketId')}
          s3EnvironmentId={wizard.getData('s3EnvironmentId')}
          onSave={handleSaveScenarioMappings}
        />
      )}
    </Box>
  );
};

// Step 5: Select Destination Moodle
const DestinationSelectionStep: React.FC = () => {
  const wizard = useWizard();
  const [sources, setSources] = useState<MoodleSource[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadSources = async () => {
      try {
        const sourcesData = await moodleApi.listSources();
        setSources(sourcesData);
      } catch (err: any) {
        wizard.setError(`Failed to load Moodle instances: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
    loadSources();
  }, []);

  useEffect(() => {
    const destinationId = wizard.getData('destinationId');
    const isValid = !!destinationId && sources.some(s => s.id === destinationId);
    wizard.setStepValidation(1, isValid);
  }, [wizard.data, sources]);

  const handleDestinationChange = (destinationId: string) => {
    wizard.setData('destinationId', destinationId);
    const destination = sources.find(s => s.id === destinationId);
    wizard.setData('destinationName', destination?.name);

    // Auto-detect and save RangeOS environment from destination
    if (destination?.rangeosEnvironmentId) {
      wizard.setData('rangeosEnvironmentId', destination.rangeosEnvironmentId);
      console.log(`Auto-detected RangeOS environment: ${destination.rangeosEnvironmentId} from destination Moodle`);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <FormControl fullWidth>
        <InputLabel>Destination Moodle Instance</InputLabel>
        <Select
          value={wizard.getData('destinationId', '')}
          onChange={(e) => handleDestinationChange(e.target.value)}
          label="Destination Moodle Instance"
        >
          {sources.map(source => (
            <MenuItem key={source.id} value={source.id}>
              <Box>
                <Typography variant="body1">{source.name}</Typography>
                {source.description && (
                  <Typography variant="caption" color="text.secondary">
                    {source.description}
                  </Typography>
                )}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {sources.length === 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          No Moodle instances configured. Please add a Moodle instance in the Config section first.
        </Alert>
      )}

      {wizard.getData('destinationId') && (
        <>
          <Alert severity="info" sx={{ mt: 2 }}>
            Selected destination: <strong>{wizard.getData('destinationName')}</strong>
          </Alert>

          {wizard.getData('rangeosEnvironmentId') && (
            <Alert severity="success" sx={{ mt: 2 }}>
              RangeOS environment auto-detected from destination Moodle configuration.
              CMI5 scenarios will be imported to this environment automatically.
            </Alert>
          )}
        </>
      )}
    </Box>
  );
};

// Step 5: Configure Restore & Resolve Conflicts
const RestoreConfigStep: React.FC = () => {
  const wizard = useWizard();
  const selectedContent: S3Content[] = wizard.getData('selectedContent', []);
  const contentTypes: string[] = wizard.getData('contentTypes', []);

  // Check if courses are being imported
  const isImportingCourses = contentTypes.includes('courses');

  // Extract unique source categories (only relevant for courses) - memoized to prevent infinite loops
  const sourceCategories = useMemo(() => {
    if (!isImportingCourses) return [];
    return Array.from(new Set(
      selectedContent.filter(c => c.type === 'course').map(c => c.category || 'Miscellaneous')
    ));
  }, [isImportingCourses, selectedContent]);

  // State for available target categories
  const [targetCategories, setTargetCategories] = useState<Array<{ id: number; name: string; path: string }>>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [restoringCategories, setRestoringCategories] = useState(false);

  // Initialize category mappings: sourceCategory → targetCategoryId
  const [categoryMappings, setCategoryMappings] = useState<Record<string, string>>(
    wizard.getData('categoryMappings',
      sourceCategories.reduce((acc, cat) => ({ ...acc, [cat]: '1' }), {})
    )
  );
  const [conflicts, setConflicts] = useState<Conflict[]>(wizard.getData('conflicts', []));
  const [loadingConflicts, setLoadingConflicts] = useState(false);
  const checkedCoursesRef = useRef<string>(''); // Track which courses we've checked

  // Check for conflicts when the step is entered
  useEffect(() => {
    const checkConflicts = async () => {
      // Only check conflicts if importing courses
      if (!isImportingCourses) {
        setLoadingConflicts(false);
        return;
      }

      // Get target Moodle ID
      const targetMoodleId = wizard.getData('destinationId');
      if (!targetMoodleId) {
        setLoadingConflicts(false);
        return;
      }

      // Extract ONLY course filenames from selected content
      const courseFilenames = selectedContent
        .filter(c => c.type === 'course')
        .map(c => c.filename);

      if (courseFilenames.length === 0) {
        setLoadingConflicts(false);
        return;
      }

      // Create a unique key for this set of courses to avoid duplicate checks
      const coursesKey = courseFilenames.sort().join(',');
      if (checkedCoursesRef.current === coursesKey) {
        // Already checked these exact courses
        return;
      }

      setLoadingConflicts(true);
      try {
        // Call the conflict detection API
        const detectedConflicts = await moodleApi.checkCourseConflicts(targetMoodleId, courseFilenames);

        setConflicts(detectedConflicts);
        wizard.setData('conflicts', detectedConflicts);
        checkedCoursesRef.current = coursesKey; // Mark as checked
      } catch (error) {
        console.error('Failed to check conflicts:', error);
      } finally {
        setLoadingConflicts(false);
      }
    };

    checkConflicts();
  }, [isImportingCourses, selectedContent]); // Re-run when importing courses or selected content changes

  // Load available categories (either from S3 or from destination Moodle)
  useEffect(() => {
    const loadCategories = async () => {
      setLoadingCategories(true);
      try {
        // Check if user selected categories as a content type
        const contentTypes: string[] = wizard.getData('contentTypes', []);
        const isRestoringCategories = contentTypes.includes('categories');

        // Get the selected environment ID (preferred) or prefix (legacy)
        const environment = wizard.getData('s3EnvironmentId') || wizard.getData('s3EnvironmentPrefix');

        // First, check if categories are being restored from S3
        const s3Categories = await contentApi.getFolderContentsWithVersions('categories', environment).catch(() => []);

        if (isRestoringCategories && s3Categories.length > 0 && s3Categories.some(f => f.name === 'categories.csv')) {
          // Categories exist in S3 AND user selected them - we're restoring categories
          setRestoringCategories(true);

          try {
            // Parse the categories.csv file to get actual category data
            const csvData = await csvApi.parseCSV('categories', 'categories.csv');

            // Build category list from CSV rows
            // Expected columns: category, parent, idnumber, description
            const categories = csvData.rows.map((row, idx) => {
              const categoryName = row.category || 'Unnamed Category';
              const parent = row.parent || '';
              const path = parent ? `${parent} / ${categoryName}` : categoryName;

              return {
                id: idx + 1, // Temporary ID for selection
                name: categoryName,
                path: path
              };
            });

            setTargetCategories(categories);
          } catch (csvError) {
            console.error('Failed to parse categories.csv:', csvError);
            // Fall back to simple file-based list
            const categories = s3Categories
              .filter(f => f.name.endsWith('.csv'))
              .map((item, idx) => ({
                id: idx + 1,
                name: item.name.replace('.csv', ''),
                path: item.name.replace('.csv', '')
              }));
            setTargetCategories(categories);
          }
        } else {
          // No categories in S3 - fetch from destination Moodle
          setRestoringCategories(false);
          const destinationId = wizard.getData('destinationId');
          if (destinationId) {
            const moodleCategories = await moodleApi.listCategories(destinationId);
            setTargetCategories(moodleCategories.map(cat => ({
              id: cat.id,
              name: cat.name,
              path: cat.path
            })));
          }
        }
      } catch (err: any) {
        console.error('Failed to load categories:', err);
        wizard.setError(`Failed to load categories: ${err.message}`);
      } finally {
        setLoadingCategories(false);
      }
    };

    loadCategories();
  }, []);

  // Smart category matching: Auto-map categories with matching names
  useEffect(() => {
    if (targetCategories.length > 0 && sourceCategories.length > 0) {
      const autoMappings: Record<string, string> = {};

      sourceCategories.forEach(sourceCategory => {
        const sourceLower = sourceCategory.toLowerCase();

        // Try to find an exact match (case-insensitive) on name
        const exactMatch = targetCategories.find(
          target => target.name.toLowerCase() === sourceLower
        );

        if (exactMatch) {
          autoMappings[sourceCategory] = exactMatch.id.toString();
        } else {
          // Try exact match on the last part of the path (leaf name)
          const pathLeafMatch = targetCategories.find(target => {
            if (target.path) {
              const leafName = target.path.split(' / ').pop()?.toLowerCase() || '';
              return leafName === sourceLower;
            }
            return false;
          });

          if (pathLeafMatch) {
            autoMappings[sourceCategory] = pathLeafMatch.id.toString();
          } else {
            // Try fuzzy matching: check if target name/path contains source or vice versa
            const fuzzyMatch = targetCategories.find(target => {
              const targetNameLower = target.name.toLowerCase();
              const targetPathLower = target.path?.toLowerCase() || '';

              return targetNameLower.includes(sourceLower) ||
                     sourceLower.includes(targetNameLower) ||
                     targetPathLower.includes(sourceLower);
            });

            if (fuzzyMatch) {
              autoMappings[sourceCategory] = fuzzyMatch.id.toString();
            } else {
              // Default to first category or ID 1
              autoMappings[sourceCategory] = targetCategories[0]?.id.toString() || '1';
            }
          }
        }
      });

      // Only update mappings if they haven't been initialized yet
      setCategoryMappings(prev => {
        // Check if we already have non-default mappings for all source categories
        const hasAllNonDefaultMappings = sourceCategories.every(cat =>
          prev[cat] && prev[cat] !== '1' && autoMappings[cat] === prev[cat]
        );
        if (hasAllNonDefaultMappings) {
          return prev;
        }

        const merged = { ...autoMappings };
        // Preserve any existing manual mappings (non-default and different from auto)
        Object.keys(prev).forEach(key => {
          if (prev[key] && prev[key] !== '1' && prev[key] !== autoMappings[key]) {
            merged[key] = prev[key];
          }
        });
        return merged;
      });
    }
  }, [targetCategories, sourceCategories]);

  useEffect(() => {
    wizard.setDataBulk({
      categoryMappings,
      conflicts
    });

    // Step is valid if:
    // - Not importing courses, OR
    // - All conflicts are resolved and all categories are mapped
    const isValid = !isImportingCourses || (
      conflicts.every(c => c.resolution) &&
      sourceCategories.every(cat => categoryMappings[cat])
    );
    wizard.setStepValidation(5, isValid);
  }, [categoryMappings, conflicts, isImportingCourses]);

  const handleResolveConflict = (conflictId: string, resolution: ConflictResolutionStrategy, newName?: string, selectedMatchesToDelete?: string[]) => {
    setConflicts(prev => prev.map(c =>
      c.id === conflictId ? { ...c, resolution, newName, selectedMatchesToDelete } : c
    ));
  };

  const handleResolveAll = (resolution: ConflictResolutionStrategy) => {
    setConflicts(prev => prev.map(c => ({ ...c, resolution })));
  };

  // Note: Moodle automatically handles duplicate courses by appending " copy 1", " copy 2", etc.
  // to the course name when importing the same course multiple times. No conflict detection needed.

  return (
    <Box>
      {isImportingCourses && (
        <>
          <Typography variant="h6" gutterBottom>
            Category Mapping
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Map source Moodle categories to target categories. Each course will be restored to its mapped category.
          </Typography>

          {restoringCategories && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Categories will be restored from S3. Select which restored category to map each source category to.
            </Alert>
          )}

          <Alert severity="success" sx={{ mb: 2 }}>
            Categories have been automatically matched by name. Review and adjust as needed.
          </Alert>

      {loadingCategories ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Source Category</TableCell>
                <TableCell># Courses</TableCell>
                <TableCell width={350}>Target Category</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sourceCategories.map((sourceCategory) => {
                const courseCount = selectedContent.filter(
                  c => (c.category || 'Miscellaneous') === sourceCategory
                ).length;

                return (
                  <TableRow key={sourceCategory}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {sourceCategory}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={courseCount} size="small" color="primary" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <FormControl fullWidth size="small">
                        <Select
                          value={categoryMappings[sourceCategory] || (targetCategories[0]?.id.toString() || '1')}
                          onChange={(e) => setCategoryMappings(prev => ({
                            ...prev,
                            [sourceCategory]: e.target.value
                          }))}
                        >
                          {targetCategories.length > 0 ? (
                            targetCategories.map((cat) => (
                              <MenuItem key={cat.id} value={cat.id.toString()}>
                                {cat.path || cat.name}
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem value="1">Miscellaneous (ID: 1)</MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {isImportingCourses && loadingConflicts ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
            Checking for conflicts...
          </Typography>
        </Box>
      ) : isImportingCourses ? (
        <ConflictResolutionPanel
          conflicts={conflicts}
          onResolve={handleResolveConflict}
          onResolveAll={handleResolveAll}
          allowBulkResolution
        />
      ) : null}
        </>
      )}

      {!isImportingCourses && (
        <Alert severity="info">
          No course-specific configuration needed. Proceed to review your selections.
        </Alert>
      )}
    </Box>
  );
};

// Step 6: Review & Confirm
const ReviewConfirmStep: React.FC = () => {
  const wizard = useWizard();
  const selectedContent: S3Content[] = wizard.getData('selectedContent', []);
  const conflicts: Conflict[] = wizard.getData('conflicts', []);
  const contentTypes: string[] = wizard.getData('contentTypes', []);

  useEffect(() => {
    wizard.setStepValidation(6, true);
  }, []);

  // Separate content by type
  const courses = selectedContent.filter(c => c.type === 'course');
  const frameworks = selectedContent.filter(c => c.type === 'framework');
  const templates = selectedContent.filter(c => c.type === 'template');
  const categories = selectedContent.filter(c => c.type === 'category');

  const sections: SummarySection[] = [
    {
      title: 'Restore Details',
      items: [
        { label: 'Source', value: wizard.getData('s3EnvironmentName', 'Unknown'), highlight: true },
        { label: 'Destination', value: wizard.getData('destinationName', 'Unknown'), highlight: true },
        ...(courses.length > 0 ? [{ label: 'Courses', value: courses.length, highlight: true }] : []),
        ...(frameworks.length > 0 ? [{ label: 'Competency Frameworks', value: frameworks.length, highlight: true }] : []),
        ...(templates.length > 0 ? [{ label: 'Learning Path Templates', value: templates.length, highlight: true }] : []),
        ...(categories.length > 0 ? [{ label: 'Categories', value: categories.length, highlight: true }] : [])
      ]
    },
    ...(courses.length > 0 ? [{
      title: 'Configuration',
      items: [
        { label: 'Target Category', value: wizard.getData('categoryId') === 'custom' ? wizard.getData('customCategoryId') : wizard.getData('categoryId') },
        { label: 'Conflicts', value: conflicts.length > 0 ? `${conflicts.length} resolved` : 'None' }
      ]
    }] : [])
  ];

  const versionSelections = wizard.getData('versionSelections', {});

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Review Restore
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Please review the restore details below before proceeding.
      </Typography>

      <ProgressSummaryPanel sections={sections} />

      {courses.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <ContentPreviewPanel
            title="Courses to Restore"
            items={courses.map(content => ({
              type: 'course',
              name: content.filename,
              size: `${(content.size / 1024 / 1024).toFixed(2)} MB`,
              version: versionSelections[content.filename] === 'current' ? 'Current' : 'Specific version'
            }))}
          />
        </Box>
      )}

      {frameworks.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <ContentPreviewPanel
            title="Competency Frameworks to Restore"
            items={frameworks.map(content => ({
              type: 'framework',
              name: content.filename,
              size: `${(content.size / 1024 / 1024).toFixed(2)} MB`,
              version: versionSelections[content.filename] === 'current' ? 'Current' : 'Specific version'
            }))}
          />
        </Box>
      )}

      {templates.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <ContentPreviewPanel
            title="Learning Path Templates to Restore"
            items={templates.map(content => ({
              type: 'template',
              name: content.filename,
              size: `${(content.size / 1024 / 1024).toFixed(2)} MB`,
              version: versionSelections[content.filename] === 'current' ? 'Current' : 'Specific version'
            }))}
          />
        </Box>
      )}

      {categories.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <ContentPreviewPanel
            title="Categories to Restore"
            items={categories.map(content => ({
              type: 'category',
              name: content.filename,
              size: `${(content.size / 1024 / 1024).toFixed(2)} MB`,
              version: versionSelections[content.filename] === 'current' ? 'Current' : 'Specific version'
            }))}
          />
        </Box>
      )}

      <Alert severity="warning" sx={{ mt: 2 }}>
        <Typography variant="body2" fontWeight="bold">
          Important:
        </Typography>
        <Typography variant="body2">
          • This operation will start a background job
        </Typography>
        <Typography variant="body2">
          • You can monitor progress in the Jobs panel
        </Typography>
        <Typography variant="body2">
          • The restore may take several minutes depending on course size
        </Typography>
      </Alert>
    </Box>
  );
};

// Main Wizard Component
export const RestoreMoodleFromS3Wizard: React.FC<RestoreMoodleFromS3WizardProps> = ({
  open,
  onClose,
  onComplete
}) => {
  const { refreshJobs, sseConnected } = useJobs();

  const handleComplete = async (data: Record<string, any>) => {
    try {
      const selectedContent: S3Content[] = data.selectedContent || [];
      const versionSelections: Record<string, string> = data.versionSelections || {};
      const categoryMappings: Record<string, string> = data.categoryMappings || {};
      const conflicts: Conflict[] = data.conflicts || [];
      const contentTypes: string[] = data.contentTypes || [];

      // Use the S3 environment ID (preferred) or prefix (legacy fallback)
      const s3EnvironmentId = data.s3EnvironmentId || data.s3EnvironmentPrefix;

      // Separate content by type
      const courses = selectedContent.filter(c => c.type === 'course');
      const frameworks = selectedContent.filter(c => c.type === 'framework');
      const templates = selectedContent.filter(c => c.type === 'template');

      let primaryJobId = '';

      // Import categories first if selected (categories must exist before courses)
      if (contentTypes.includes('categories')) {
        try {
          console.log('Importing categories from S3...');
          const result = await moodleApi.importCategoriesFromS3(
            data.destinationId,
            'categories/categories.csv',
            s3EnvironmentId
          );
          console.log(`Categories import result: ${result.message}`);

          // Manually refresh jobs
          try {
            await refreshJobs();
          } catch (err) {
            console.warn('Failed to refresh jobs after category import:', err);
          }
        } catch (err: any) {
          console.error('Failed to import categories:', err);
          throw new Error(`Failed to import categories: ${err.message}`);
        }
      }

      // Import courses if selected
      if (contentTypes.includes('courses') && courses.length > 0) {
        // Build per-course category mapping: filename → categoryId
        const courseCategoryMap: Record<string, number> = {};
        courses.forEach(course => {
          const sourceCategory = course.category || 'Miscellaneous';
          const targetCategoryId = categoryMappings[sourceCategory] || '1';
          courseCategoryMap[course.filename] = parseInt(targetCategoryId, 10);
        });

        // Build conflict resolutions map: filename → {resolution, newName?, selectedMatchesToDelete?}
        const conflictResolutions: Record<string, { resolution: string; newName?: string; selectedMatchesToDelete?: string[] }> = {};
        conflicts.forEach(conflict => {
          if (conflict.resolution) {
            conflictResolutions[conflict.filename || conflict.itemName] = {
              resolution: conflict.resolution,
              newName: conflict.newName,
              selectedMatchesToDelete: conflict.selectedMatchesToDelete
            };
          }
        });

        // Check if we have CMI5 courses and a RangeOS environment selected
        const hasCMI5Activities = data.hasCMI5Activities || false;
        const cmi5Courses = data.cmi5Courses || [];
        const rangeosEnvironmentId = data.rangeosEnvironmentId;
        const scenarioMappings = data.scenarioMappings;

        // Separate courses with CMI5 from regular courses
        const cmi5CourseFilenames = new Set(cmi5Courses.map((c: any) => c.courseFilename));
        const coursesWithCMI5 = courses.filter(c => cmi5CourseFilenames.has(c.filename));
        const regularCourses = courses.filter(c => !cmi5CourseFilenames.has(c.filename));

        // Import courses with CMI5 using comprehensive restore (one at a time, as background jobs)
        if (coursesWithCMI5.length > 0 && rangeosEnvironmentId) {
          console.log(`Importing ${coursesWithCMI5.length} courses with CMI5 activities and scenarios...`);

          for (const course of coursesWithCMI5) {
            try {
              const categoryId = courseCategoryMap[course.filename];
              const result = await moodleApi.restoreCourseWithCMI5(
                data.destinationId,
                course.filename,
                categoryId,
                s3EnvironmentId,
                rangeosEnvironmentId,
                scenarioMappings
              );

              console.log(`  ✓ ${course.filename}: Started background job ${result.jobId}`);

              // Store the first job ID as the primary job ID
              if (!primaryJobId) {
                primaryJobId = result.jobId;
              }
            } catch (err) {
              console.error(`Failed to start restore for course with CMI5: ${course.filename}`, err);
            }
          }

          // Manually refresh jobs to ensure the new job appears in the Jobs Panel
          try {
            await refreshJobs();
          } catch (err) {
            console.warn('Failed to refresh jobs after CMI5 restore creation:', err);
          }
        }

        // Import regular courses using batch import
        if (regularCourses.length > 0) {
          const regularS3Keys = regularCourses.map(c => c.filename);
          const regularCategoryMap: Record<string, number> = {};
          regularCourses.forEach(course => {
            regularCategoryMap[course.filename] = courseCategoryMap[course.filename];
          });

          // Start the course restore job for regular courses
          const result = await moodleApi.importCoursesFromS3WithProgress(
            data.destinationId,
            regularS3Keys,
            regularCategoryMap,
            s3EnvironmentId,
            conflictResolutions
          );

          primaryJobId = result.jobId || '';
        }

        // Manually refresh jobs to ensure the new job appears in the Jobs Panel
        try {
          await refreshJobs();
        } catch (err) {
          console.warn('Failed to refresh jobs after creation:', err);
        }
      }

      // Import competency frameworks if selected
      if (contentTypes.includes('frameworks') && frameworks.length > 0) {
        // Build full S3 paths for frameworks (including "content/" prefix)
        const frameworkKeys = frameworks.map(f => {
          const versionId = versionSelections[f.filename];
          if (versionId && versionId !== 'current') {
            return `content/competency-frameworks/versions/${f.filename}/${versionId}`;
          }
          return `content/competency-frameworks/current/${f.filename}`;
        });

        const result = await moodleApi.importCompetencyFrameworksFromS3WithProgress(
          data.destinationId,
          frameworkKeys,
          s3EnvironmentId
        );

        // If we don't have a primary job ID yet, use this one
        if (!primaryJobId) {
          primaryJobId = result.jobId;
        }

        // Manually refresh jobs
        try {
          await refreshJobs();
        } catch (err) {
          console.warn('Failed to refresh jobs after creation:', err);
        }
      }

      // Import learning path templates if selected
      if (contentTypes.includes('learning-paths') && templates.length > 0) {
        // Build full S3 paths for templates (including "content/" prefix)
        const templateKeys = templates.map(t => {
          const versionId = versionSelections[t.filename];
          if (versionId && versionId !== 'current') {
            return `content/learning-path-templates/versions/${t.filename}/${versionId}`;
          }
          return `content/learning-path-templates/current/${t.filename}`;
        });

        const result = await moodleApi.importLearningPathTemplatesFromS3WithProgress(
          data.destinationId,
          templateKeys,
          s3EnvironmentId
        );

        // If we don't have a primary job ID yet, use this one
        if (!primaryJobId) {
          primaryJobId = result.jobId;
        }

        // Manually refresh jobs
        try {
          await refreshJobs();
        } catch (err) {
          console.warn('Failed to refresh jobs after creation:', err);
        }
      }

      // Always call onComplete to close the wizard
      if (onComplete) {
        onComplete(primaryJobId);
      }
    } catch (err: any) {
      throw new Error(`Failed to start restore: ${err.message}`);
    }
  };

  return (
    <WizardDialog
      open={open}
      onClose={onClose}
      title="Restore Moodle from S3 Wizard"
      steps={WIZARD_STEPS}
      onComplete={handleComplete}
      width="lg"
      persistenceKey="restore-moodle-from-s3-wizard"
      showResumePrompt
    >
      <WizardStep stepIndex={0}>
        <S3SourceSelectionStep />
      </WizardStep>
      <WizardStep stepIndex={1}>
        <DestinationSelectionStep />
      </WizardStep>
      <WizardStep stepIndex={2}>
        <ContentSelectionStep />
      </WizardStep>
      <WizardStep stepIndex={3}>
        <VersionSelectionStep />
      </WizardStep>
      <WizardStep stepIndex={4}>
        <ScenarioMappingStep />
      </WizardStep>
      <WizardStep stepIndex={5}>
        <RestoreConfigStep />
      </WizardStep>
      <WizardStep stepIndex={6}>
        <ReviewConfirmStep />
      </WizardStep>
    </WizardDialog>
  );
};
